import React, {
    Children,
    useEffect,
    useRef,
    useState,
    useContext,
    useCallback,
    useMemo,
} from "react";
import "./NFLookupCatalog.css";
import {TextBox, Button as TextBoxButton} from "devextreme-react/text-box";
import ScrollView from "devextreme-react/scroll-view";
import {Popover} from "devextreme-react";
import {
    Controller,
    useFormContext,
    useFormState,
    useWatch,
} from "react-hook-form";
import {ValidationStatus} from "devextreme/common";
import {NewCatalogModalView} from "../NewCatalogModalView";
import DropDownArrowIcon from "../../../assets/img/svg/DropDownArrowIcon";
import Search from "../../../assets/img/svg/Search";
import Loader from "../../../assets/img/svg/Loader";
import {IAppEntity} from "../../../config/entities";
import {ERP_dateTimeFormat} from "../../../config/appconst";
import {format} from "date-fns";

interface IProps {
    title?: string;
    fieldName: string;
    entity: IAppEntity;
    children?: React.ReactNode;
    serverData?: (searchText: string | undefined) => Promise<any[]>;
    required?: boolean;
    showBorder?: boolean;
    width?: number;
    index?: number;
    tableName?: string;
    tableFieldName?: string;
    onValueChanged?: (e: any, index: number) => void;
    value?: any;
    displayFieldName?: string;
    selectedCatalog?: boolean;
    setSelectedCatalog?: (value: boolean) => void;
}

/// TODO: When user removes the row before last row, the last row's LookupCatalog value is not updated. Maybe problem is in updating SearchText field.
export const NFLookupDocument = ({
                                     fieldName,
                                     entity,
                                     title,
                                     children,
                                     serverData,
                                     required,
                                     showBorder = true,
                                     width,
                                     index = 0,
                                     tableFieldName,
                                     tableName,
                                     onValueChanged,
                                     value,
                                     displayFieldName = "documentNumber",
                                     selectedCatalog,
                                     setSelectedCatalog,
                                 }: IProps) => {
    const {register, control, watch, formState} = useFormContext();
    const {errors, dirtyFields} = useFormState();

    const [currentId, setCurrentId] = useState<string | undefined>(undefined);

    const [validationStatus, setValidationStatus] =
        useState<ValidationStatus>("valid");

    useEffect(() => {
        setCurrentId(watch(fieldName));
    }, [watch(fieldName)]);

    useEffect(() => {
        if (currentId) {
            loadDefaultItem();
        }
    }, [currentId]);

    const [selectedItem, setSelectedItem] = useState<any>(undefined);
    const [isPopoverVisible, setIsPopoverVisible] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>("");
    const [selectedItemId, setSelectedItemId] = useState<string>("");
    const [catalogModalViewOpen, setCatalogModalViewOpen] =
        useState<boolean>(false);

    const [detailModalViewOpen, setDetailModalViewOpen] =
        useState<boolean>(false);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const [items, setItems] = useState<any[]>([]);
    const popoverRef = useRef<Popover>(null);

    const componentId = useMemo(
        () =>
            Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15),
        []
    );

    const arrayChildren = useMemo(() => Children.toArray(children), [children]);

    // const { selectedCatalog, setSelectedCatalog, searchText, setSearchText } =
    //   useContext(CatalogContext);

    const loadItems = useCallback(
        async (searchText: string | undefined) => {
            try {
                setIsLoading(true);
                setIsError(false);
                setErrorMessage("");

                let filteredItems: any[] = [];

                if (serverData) {
                    const loadedItems = await serverData(searchText);
                    console.log("loadeditems", loadedItems);
                    filteredItems = loadedItems.filter((item) =>
                        item.documentNumber
                            .toLowerCase()
                            .includes(searchText?.toLowerCase() ?? "")
                    );
                } else if (entity) {
                    const dataService = entity.getDataService();
                    const loadedItems = await dataService.GetAll();
                    filteredItems = loadedItems.filter((item: any) =>
                        item.documentNumber
                            .toLowerCase()
                            .includes(searchText?.toLowerCase() ?? "")
                    );
                }

                setItems(filteredItems);
            } catch (e: any) {
                setIsError(true);
                setErrorMessage(e.message);
                setItems([]);
            } finally {
                setIsLoading(false);
            }
        },
        [entity, serverData]
    );

    const getItemDescription =
        (item: any) => {
            if (item) {
                return `${entity.title} ${item["documentNumber"]} from ${format(item["documentDate"], ERP_dateTimeFormat)}`;
            } else {
                return "...Not selected...";
            }
        }

    const loadDefaultItem = useCallback(async () => {
        const item = await entity.getDataService().Get(currentId);
        setSelectedItemId(item.id);
        setSelectedItem(item);
        // setSearchText(item ? item[displayFieldName] : "");
        setSearchText(getItemDescription(item));
    }, [currentId, displayFieldName, entity]);

    // useEffect(() => {
    //     if (model && !selectedItem && model[fieldName]) {
    //         loadDefaultItem();
    //     }
    // }, [model]);

    const handlePopoverHidden = useCallback(() => {
        setIsPopoverVisible(false);
        if (!selectedItem) return;
        if (selectedItem[displayFieldName] !== searchText) {
            setSearchText(selectedItem ? selectedItem[displayFieldName] : "");
        }
    }, [selectedItem, searchText, displayFieldName]);

    const handleSelectItem = useCallback(
        (item: any) => {
            setSelectedItem(item);
            setSearchText(item ? item[displayFieldName] : "");

            setIsPopoverVisible(false);
            // if (onFieldChange) {
            //     onFieldChange(fieldName, item.id);
            // }
        },
        [displayFieldName, setSelectedItem, setSearchText]
    );

    const handleSearchTextChange = useCallback(async (inputEvent: any) => {
        const newSearchTextValue = inputEvent.event.originalEvent.target.value;

        if (newSearchTextValue !== searchText) {
            setSearchText(newSearchTextValue);

            setIsPopoverVisible(true);
            await loadItems(newSearchTextValue);
        }
    }, []);

    const handleCatalogModalViewClosed = useCallback(() => {
        setCatalogModalViewOpen(false);
    }, [loadItems, searchText]);

    const handleDetailModalViewClose = useCallback(() => {
        setDetailModalViewOpen(false);
    }, []);

    useEffect(() => {
        if (tableName && tableFieldName) {
            const errorTable = formState.errors[tableName];
            if (errorTable) {
                //@ts-ignore
                const errorLine = errorTable[index];
                if (errorLine) {
                    const errorField = errorLine[tableFieldName];
                    if (errorField) {
                        setValidationStatus("invalid");
                        return;
                    }
                }
            }
            setValidationStatus("valid");
        } else {
            const status = formState.errors[fieldName]
                ? "invalid"
                : dirtyFields[fieldName]
                    ? "valid"
                    : "valid";
            setValidationStatus(status);
        }
    }, [formState]);

    return (
        <>
            <Controller
                control={control}
                name={fieldName}
                rules={{
                    required: required === true,
                    onChange: (newValue: any) => {
                        const newId = newValue?.target.value;

                        if (newId) {
                            const newItem = items.find((x) => x.id === newId);
                            handleSelectItem(newItem);

                            setSearchText(newItem[displayFieldName]);
                        } else {
                            handleSelectItem(undefined);
                            setSearchText("");
                        }
                        if (onValueChanged) {
                            onValueChanged(newValue.target.value, index);
                        }
                    },
                }}
                render={({field: {onChange, onBlur, value, ref}}) => {
                    return (
                        <>
                            <div className={title ? "form-group" : ""}>
                                {title && <label className={"form-label-sm"}>{title}</label>}
                                <TextBox
                                    value={searchText}
                                    name={`searchTextBox${componentId}`}
                                    width={width || undefined}
                                    style={{border: showBorder === true ? "block" : "none"}}
                                    id={`searchTextBox${componentId}`}
                                    validationStatus={validationStatus}
                                    onInput={(e) => handleSearchTextChange(e)}
                                    className="bg-light d-flex flex-row-reverse"
                                >
                                    <div className="d-flex justify-content-end align-items-center">
                                        <div
                                            onClick={() => {
                                                setIsPopoverVisible(!isPopoverVisible);
                                                loadItems(searchText);
                                            }}
                                        >
                                            <DropDownArrowIcon className="nf-lookup-icon"/>
                                        </div>

                                        <div className="vr nf-lookupVr"></div>
                                        <div
                                            onClick={() => {
                                                if (selectedItem) {
                                                    setCatalogModalViewOpen(true);
                                                    setSelectedItem(selectedItem);
                                                }
                                            }}
                                        >
                                            <Search className="nf-lookup-icon"/>
                                        </div>
                                        <div className="vr nf-lookupVr"></div>
                                        <div
                                            onClick={async () => {
                                                onChange(undefined);
                                                if (isPopoverVisible) {
                                                    await loadItems("");
                                                    console.log("loaditems");
                                                }
                                                if (setSelectedCatalog) {
                                                    setSelectedCatalog(false);
                                                    console.log("closing");
                                                }
                                            }}
                                        >
                                            <svg
                                                viewBox="0 0 48 48"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="nf-lookup-icon"
                                            >
                                                <path
                                                    d="m12.45 37.65-2.1-2.1L21.9 24 10.35 12.45l2.1-2.1L24 21.9l11.55-11.55 2.1 2.1L26.1 24l11.55 11.55-2.1 2.1L24 26.1Z"></path>
                                            </svg>
                                        </div>
                                    </div>
                                </TextBox>
                            </div>
                            <Popover
                                target={`#searchTextBox${componentId}`}
                                position={"bottom"}
                                onHidden={handlePopoverHidden}
                                ref={popoverRef}
                                visible={isPopoverVisible}
                            >
                                {isLoading ? (
                                    <Loader/>
                                ) : (
                                    <div
                                        className={"lookup-catalog-popover-table popover-content"}
                                    >
                                        <ScrollView>
                                            <div className="table-wrapper-scroll-y">
                                                <table className="table bg-light">
                                                    <thead>
                                                    <tr>
                                                        {arrayChildren.map((field: any, index) => {
                                                            return (
                                                                <th key={index}>{field.props.caption}</th>
                                                            );
                                                        })}
                                                    </tr>
                                                    </thead>

                                                    <tbody>
                                                    {items?.map((item: any, index) => (
                                                        <tr
                                                            key={index}
                                                            style={{cursor: "pointer"}}
                                                            onClick={() => onChange(item?.id)}
                                                        >
                                                            {arrayChildren.map((field: any, index) => {
                                                                return (
                                                                    <td key={index}>
                                                                        {item[field.props.fieldName]}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </ScrollView>

                                        {/*{isLoading && <div className={"w-100"}>Loading...</div>}*/}
                                        {/*{isError && <div className={"alert alert-danger"}>{errorMessage}</div>}*/}
                                        {items?.length === 0 && (
                                            <div className={"popover-text mb-3"}>No items</div>
                                        )}
                                        <div className="d-flex justify-content-center mt-2">
                                            <div
                                                className="link-dark add-new-btn"
                                                onClick={() => {
                                                    setDetailModalViewOpen(true);
                                                    setIsPopoverVisible(false);
                                                }}
                                            >
                                                Create new...
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Popover>
                            {selectedItem && catalogModalViewOpen && (
                                <NewCatalogModalView
                                    id={selectedItem.id}
                                    entity={entity}
                                    open={catalogModalViewOpen}
                                    onClose={handleCatalogModalViewClosed}
                                    setSearchText={setSearchText}
                                    setSelectedItem={setSelectedItem}
                                />
                            )}

                            {detailModalViewOpen && (
                                <NewCatalogModalView
                                    id={selectedItem?.id}
                                    entity={entity}
                                    open={detailModalViewOpen}
                                    onClose={handleDetailModalViewClose}
                                    setSearchText={setSearchText}
                                    setSelectedItem={setSelectedItem}
                                />
                            )}
                        </>
                    );
                }}
            />
        </>
    );
};
